// src/oura/hooks/useMerchantDocuments.ts
//
// The documents shared with the merchant, kept fresh.
//
// The client page arrives once, with `documents`. A doc shared, unshared or
// answered later changes that list, so the screens that show it (Decisions,
// Home) and the Decisions badge read the page again when it may have moved:
//   - after any change the documents service announces (subscribeDocs),
//   - when the tab comes back to the front,
//   - every 60 seconds while the tab is visible,
// and only while the page carries `documents` at all: a business Oura does not
// read docs for costs no extra request.
//
// Every re-read goes through invalidateClientPage(), so the badge and the
// screen share one read and never disagree about a count: the workspace
// takes each fresh page whole (pages/oura-workspace), so the questions and
// threads move with the documents. One timer serves every reader on screen.

import { useCallback, useEffect, useRef, useState } from "react";
import { subscribeDocs, type MerchantDocument } from "@service/aeo/clientDocs";
import { getClientPage, invalidateClientPage, subscribeClientPage, type ClientPagePayload } from "@service/aeo/clientReview";

const EVERY = 60_000;

/** Whether the last page read carried `documents`. The timer only reads again while it did. */
let docsServed = false;
let users = 0;
let stopTimer: (() => void) | null = null;

/** Record what the latest client page read carried. */
export function noteDocsServed(payload: Pick<ClientPagePayload, "documents">): void {
    docsServed = payload.documents !== undefined;
}

function readAgainIfVisible(): void {
    if (docsServed && document.visibilityState === "visible") invalidateClientPage();
}

/**
 * Keep the merchant's page fresh for as long as the returned release is not
 * called. Counted, so the badge and a screen share one timer.
 */
export function keepMerchantDocsFresh(): () => void {
    users += 1;
    if (users === 1) {
        // A write or a sample-store event on the documents side: the page's list may have changed.
        const offDocs = subscribeDocs(() => invalidateClientPage());
        const timer = window.setInterval(readAgainIfVisible, EVERY);
        const onVisibility = () => {
            if (document.visibilityState === "visible") readAgainIfVisible();
        };
        document.addEventListener("visibilitychange", onVisibility);
        stopTimer = () => {
            offDocs();
            window.clearInterval(timer);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }
    let released = false;
    return () => {
        if (released) return;
        released = true;
        users -= 1;
        if (users === 0) {
            stopTimer?.();
            stopTimer = null;
        }
    };
}

export interface MerchantDocs {
    /** Undefined: Oura does not read docs for this business yet. [] : nothing is shared with them. */
    documents: MerchantDocument[] | undefined;
    /** The last read again failed. `documents` is the last good list. */
    refreshFailed: boolean;
    /** Counts every finished read again, good or failed, so a caller can wait for the next one. */
    reads: number;
    /** Read the page again now. */
    refresh: () => void;
}

interface Held {
    base: ClientPagePayload;
    documents: MerchantDocument[] | undefined;
    failed: boolean;
}

/**
 * The page's `documents`, replaced by each fresher read. A new payload (the
 * screen refreshed after an answer) starts over from its own list.
 */
export function useMerchantDocuments(payload: ClientPagePayload): MerchantDocs {
    const [held, setHeld] = useState<Held>(() => ({ base: payload, documents: payload.documents, failed: false }));
    const current = held.base === payload ? held : { base: payload, documents: payload.documents, failed: false };
    if (held.base !== payload) setHeld(current);
    const [reads, setReads] = useState(0);
    // The workspace takes each fresh page as a new `payload`, which re-runs the
    // effect below mid-read: the read still counts, or a caller waiting on
    // `reads` (a ?doc= link) would wait for ever.
    const alive = useRef(true);
    useEffect(() => {
        alive.current = true;
        return () => {
            alive.current = false;
        };
    }, []);
    // Reads overlap (two docs events, an event and a refresh): only the newest
    // one lands and counts, so an older read that comes back late never puts
    // an older list on screen, and a caller waiting on `reads` sees the newest.
    const readSeq = useRef(0);

    // A staffer's preview reads another business; only the merchant's own page is read again here.
    const own = !payload.previewedByStaff;
    useEffect(() => {
        if (!own) return;
        noteDocsServed(payload);
        let cancelled = false;
        const read = () => {
            const mine = ++readSeq.current;
            getClientPage().then(
                (r) => {
                    if (!alive.current || mine !== readSeq.current) return;
                    if (!cancelled) {
                        if (r.ok) {
                            noteDocsServed(r.payload);
                            setHeld((h) => ({ base: h.base, documents: r.payload.documents, failed: false }));
                        } else setHeld((h) => ({ ...h, failed: true }));
                    }
                    setReads((n) => n + 1);
                },
                () => {
                    if (!alive.current || mine !== readSeq.current) return;
                    if (!cancelled) setHeld((h) => ({ ...h, failed: true }));
                    setReads((n) => n + 1);
                },
            );
        };
        const unsubscribe = subscribeClientPage(read);
        const release = keepMerchantDocsFresh();
        return () => {
            cancelled = true;
            unsubscribe();
            release();
        };
    }, [payload, own]);

    // A preview has nothing to read again: count it as read, so a caller waiting on it moves on.
    const refresh = useCallback(() => (own ? invalidateClientPage() : setReads((n) => n + 1)), [own]);
    return { documents: current.documents, refreshFailed: current.failed, reads, refresh };
}
